import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import WheelCanvas from '../components/WheelCanvas';
import HabboImage from '../components/HabboImage';
import ColorPicker from '../components/ColorPicker';
import { searchFurniItems, getProfileData } from '../services/api';
import { fetchProfileColor, getAvatarBgStyle } from '../services/profileColorService';
import { FurniIcon, WinnerImage } from '../components/WheelWinnerImage';

// ─── Constants ───────────────────────────────────────────────────────────────

const FONT_OPTIONS = [
    { label: 'Jersey 10', value: "'Jersey 10', sans-serif" },
    { label: 'Volter Bold', value: 'VolterBoldGoldfish, sans-serif' },
    { label: 'Instrument Serif', value: "'Instrument Serif', serif" },
    { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
    { label: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
    { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
    { label: 'Georgia', value: 'Georgia, serif' },
    { label: 'Times New Roman', value: "'Times New Roman', Times, serif" },
    { label: 'Courier New', value: "'Courier New', Courier, monospace" },
    { label: 'Impact', value: 'Impact, Haettenschweiler, sans-serif' },
    { label: 'Comic Sans MS', value: "'Comic Sans MS', cursive, sans-serif" },
];

const DEFAULT_PALETTE = [
    '#C0392B',
    '#2471A3',
    '#1E8449',
    '#D4AC0D',
    '#7D3C98',
    '#117A65',
    '#A04000',
    '#1A5276',
];

const TEXT_SIZE_MUL = 1.35; // fixed — no UI option

const SPIN_DURATION_OPTIONS = [
    { label: '3s', value: 3000 },
    { label: '5s', value: 5000 },
    { label: '8s', value: 8000 },
    { label: '12s', value: 12000 },
];

const TEXT_ORIENT_OPTIONS = [
    { label: 'Radial', value: 'radial' },
    { label: 'Horizontal', value: 'horizontal' },
];

const ICON_NUMBERS = Array.from({ length: 359 }, (_, i) => i + 1);
const LS_KEY = 'franklywheel_v3';

function uid() { return Math.random().toString(36).slice(2, 10); }

function postWheelMessage(key, msg) {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl || !key) return;
    fetch(`${apiUrl}/wheel/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, ...msg }),
    }).catch(() => {});
}


function getOrCreateSessionKey() {
    try {
        const k = localStorage.getItem('franklywheel_session');
        if (k) return k;
        const next = uid() + uid();
        localStorage.setItem('franklywheel_session', next);
        return next;
    } catch { return uid(); }
}

function encodeState(state) {
    try { return btoa(encodeURIComponent(JSON.stringify(state))); } catch { return ''; }
}

// ─── Module-level habbo info cache ────────────────────────────────────────────

const habboInfoCache = {};

async function getHabboInfo(name) {
    const key = name.toLowerCase();
    if (habboInfoCache[key]) return habboInfoCache[key];
    const [bg, profileResult] = await Promise.all([
        fetchProfileColor(name),
        getProfileData(name, 'customisation').catch(() => null),
    ]);
    const info = { bg, isMember: !!profileResult?.success };
    habboInfoCache[key] = info;
    return info;
}

// ─── Furni Search Modal ───────────────────────────────────────────────────────

function FurniSearchModal({ open, onClose, onSelect }) {
    const [term, setTerm] = useState('');
    const [results, setResults] = useState([]);
    const [state, setState] = useState('idle');
    const inputRef = useRef(null);
    const debRef = useRef(null);
    const abRef = useRef(null);

    useEffect(() => {
        if (open) { setTerm(''); setResults([]); setState('idle'); setTimeout(() => inputRef.current?.focus(), 50); }
    }, [open]);

    useEffect(() => {
        const q = term.trim();
        if (q.length < 3) { setResults([]); setState('idle'); clearTimeout(debRef.current); return; }
        clearTimeout(debRef.current);
        debRef.current = setTimeout(async () => {
            if (abRef.current) abRef.current.abort();
            const ac = new AbortController();
            abRef.current = ac;
            setState('loading');
            try {
                const data = await searchFurniItems(q, ac.signal);
                if (ac.signal.aborted) return;
                if (data?.success && Array.isArray(data.items)) {
                    setResults(data.items.map(it => ({ value: it.id, label: it.name, code: it.code })));
                    setState('ready');
                } else { setState('error'); }
            } catch (e) {
                if (!e?.name?.includes('Abort')) setState('error');
            }
        }, 500);
        return () => clearTimeout(debRef.current);
    }, [term]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
            <div className="bg-offwhite border-2 border-black w-[min(480px,92vw)] max-h-[80vh] flex flex-col" style={{ boxShadow: '4px 4px 0 rgba(0,0,0,0.8)' }} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-3 py-2 border-b border-black">
                    <span className="font-['Jersey_10'] uppercase text-xl tracking-widest">Search Furni</span>
                    <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center border border-black bg-gray-200 hover:bg-red-500 hover:text-white font-['Jersey_10'] text-lg leading-none">×</button>
                </div>
                <div className="p-2 border-b border-black/10">
                    <div className="relative">
                        <input ref={inputRef} type="text" value={term} onChange={e => setTerm(e.target.value)}
                            placeholder="Type 3+ letters…"
                            className="w-full px-3 py-2 border border-black text-base bg-white pr-8 placeholder:text-black/40 focus:outline-none" />
                        {state === 'loading' && <div className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />}
                        {state !== 'loading' && term && <button type="button" onClick={() => setTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-black/40 hover:text-black text-xl leading-none">×</button>}
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto habbo-scroll-v p-3 min-h-0">
                    {state === 'idle' && <p className="text-center py-8 font-['Jersey_10'] text-base opacity-40 uppercase">Type at least 3 letters</p>}
                    {state === 'loading' && <div className="flex justify-center py-10"><div className="w-8 h-8 border-4 border-black/20 border-t-black rounded-full animate-spin" /></div>}
                    {state === 'error' && <p className="text-center py-8 font-['Jersey_10'] text-base text-red-600 uppercase">Search failed</p>}
                    {state === 'ready' && results.length === 0 && <p className="text-center py-8 font-['Jersey_10'] text-base opacity-40 uppercase">No results for "{term}"</p>}
                    {state === 'ready' && results.length > 0 && (
                        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, 72px)' }}>
                            {results.map(f => (
                                <button key={f.value} type="button" title={f.label} onClick={() => onSelect(f)}
                                    className="flex flex-col items-center border border-gray-300 bg-gray-100 hover:bg-yellow-100 hover:border-black transition-colors overflow-hidden" style={{ width: 72 }}>
                                    <div className="w-full flex items-center justify-center overflow-hidden bg-gray-200" style={{ height: 56 }}>
                                        <FurniIcon code={f.code} size={52} />
                                    </div>
                                    <div className="w-full px-0.5 py-0.5 text-center font-['Jersey_10'] text-base leading-tight truncate">{f.label}</div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Icon Picker Modal ────────────────────────────────────────────────────────

function IconPickerModal({ open, onClose, onSelect }) {
    const [filter, setFilter] = useState('');
    const [page, setPage] = useState(0);
    const PER_PAGE = 80;

    const filtered = useMemo(() => {
        if (!filter.trim()) return ICON_NUMBERS;
        return ICON_NUMBERS.filter(i => String(i).startsWith(filter.trim()));
    }, [filter]);

    const paged = filtered.slice(page * PER_PAGE, (page + 1) * PER_PAGE);
    const maxPage = Math.max(0, Math.ceil(filtered.length / PER_PAGE) - 1);

    useEffect(() => { if (open) { setFilter(''); setPage(0); } }, [open]);
    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
            <div className="bg-offwhite border-2 border-black w-[min(560px,92vw)] max-h-[80vh] flex flex-col" style={{ boxShadow: '4px 4px 0 rgba(0,0,0,0.8)' }} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-3 py-2 border-b border-black">
                    <span className="font-['Jersey_10'] uppercase text-xl tracking-widest">Pick Icon</span>
                    <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center border border-black bg-gray-200 hover:bg-red-500 hover:text-white font-['Jersey_10'] text-lg leading-none">×</button>
                </div>
                <div className="p-2 border-b border-black/10">
                    <input type="text" value={filter} onChange={e => { setFilter(e.target.value); setPage(0); }}
                        placeholder="Filter by number…"
                        className="w-full px-3 py-2 border border-black text-base bg-white placeholder:text-black/40 focus:outline-none" />
                </div>
                <div className="flex-1 overflow-y-auto habbo-scroll-v p-3 min-h-0">
                    <div className="flex flex-wrap gap-1">
                        {paged.map(n => (
                            <button key={n} type="button" title={`icon_${n}.png`}
                                onClick={() => { onSelect(n); onClose(); }}
                                className="border border-gray-200 bg-gray-50 hover:bg-yellow-100 hover:border-black overflow-hidden flex items-center justify-center transition-colors"
                                style={{ width: 44, height: 44 }}>
                                <img src={`${process.env.PUBLIC_URL}/img/icons/icon_${n}.png`} alt={`icon ${n}`}
                                    onError={e => { e.currentTarget.parentElement.style.display = 'none'; }} />
                            </button>
                        ))}
                    </div>
                </div>
                {maxPage > 0 && (
                    <div className="flex items-center justify-center gap-3 px-3 py-2 border-t border-black/10">
                        <button type="button" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                            className="px-3 py-1 border border-black font-['Jersey_10'] text-base disabled:opacity-30 hover:bg-black hover:text-white transition-colors">Prev</button>
                        <span className="font-['Jersey_10'] text-base">{page + 1} / {maxPage + 1}</span>
                        <button type="button" onClick={() => setPage(p => Math.min(maxPage, p + 1))} disabled={page === maxPage}
                            className="px-3 py-1 border border-black font-['Jersey_10'] text-base disabled:opacity-30 hover:bg-black hover:text-white transition-colors">Next</button>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Habbo Segment Card ───────────────────────────────────────────────────────

function HabboSegmentCard({ seg, onUpdate, onRemove, colorSwatch }) {
    const [editing, setEditing] = useState(false);
    const [nameInput, setNameInput] = useState(seg.habboName || '');
    const [habboInfo, setHabboInfo] = useState({ isMember: false, bg: '#F2F2F2', loading: !!seg.habboName });

    useEffect(() => {
        if (!seg.habboName) return;
        let cancelled = false;
        setHabboInfo(prev => ({ ...prev, loading: true }));
        getHabboInfo(seg.habboName).then(info => {
            if (!cancelled) {
                setHabboInfo({ ...info, loading: false });
                // Persist resolved bgColor on segment for WheelCanvas
                if (seg.bgColor !== info.bg || seg.isMember !== info.isMember) {
                    onUpdate({ ...seg, bgColor: info.bg, isMember: info.isMember });
                }
            }
        }).catch(() => {
            if (!cancelled) setHabboInfo({ isMember: false, bg: '#F2F2F2', loading: false });
        });
        return () => { cancelled = true; };
    }, [seg.habboName]); // eslint-disable-line

    const commitName = () => {
        const v = nameInput.trim();
        if (v) onUpdate({ ...seg, habboName: v, label: v });
        setEditing(false);
    };

    return (
        <div className="border border-black/20 bg-white flex items-center gap-2 p-2 group">
            <div className="shrink-0 w-2 self-stretch rounded-sm" style={{ background: colorSwatch }} />
            <div className="shrink-0 overflow-hidden flex items-center justify-center border-r border-black/10"
                style={{ width: 42, height: 60, ...getAvatarBgStyle(habboInfo.isMember ? habboInfo.bg : '#F2F2F2') }}>
                {seg.habboName ? (
                    <HabboImage habbo={seg.habboName} size="a" direction={2} head_direction={2} shadow={false} />
                ) : (
                    <span className="text-base font-['Jersey_10'] text-black/30 uppercase">?</span>
                )}
            </div>
            <div className="flex-1 min-w-0">
                {editing ? (
                    <input autoFocus type="text" value={nameInput}
                        onChange={e => setNameInput(e.target.value)}
                        onBlur={commitName}
                        onKeyDown={e => e.key === 'Enter' && commitName()}
                        className="w-full border border-black px-2 py-1 text-base font-sans bg-white focus:outline-none" />
                ) : (
                    <button type="button" onClick={() => { setNameInput(seg.habboName); setEditing(true); }}
                        className="font-['Jersey_10'] text-base text-left w-full truncate hover:underline block">
                        {seg.habboName || 'Click to set name'}
                    </button>
                )}
                {!habboInfo.loading && (
                    <span className="font-['Jersey_10'] text-sm text-black/40 uppercase">
                        {habboInfo.isMember ? 'Frankly Member' : 'Not a member'}
                    </span>
                )}
                {habboInfo.loading && (
                    <span className="font-['Jersey_10'] text-sm text-black/30 uppercase">Checking…</span>
                )}
            </div>
            <button type="button" onClick={onRemove}
                className="shrink-0 w-7 h-7 flex items-center justify-center border border-black/20 text-black/40 hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors font-['Jersey_10'] text-lg leading-none opacity-0 group-hover:opacity-100">×</button>
        </div>
    );
}

// ─── Furni Segment Card ───────────────────────────────────────────────────────

function FurniSegmentCard({ seg, onUpdate, onRemove, colorSwatch }) {
    const [showSearch, setShowSearch] = useState(false);
    const [editLabel, setEditLabel] = useState(false);
    const [labelInput, setLabelInput] = useState(seg.label || '');

    const addFurni = (f) => {
        const already = seg.furnis.find(x => x.code === f.code);
        if (already) {
            onUpdate({ ...seg, furnis: seg.furnis.map(x => x.code === f.code ? { ...x, qty: x.qty + 1 } : x) });
        } else {
            onUpdate({ ...seg, furnis: [...seg.furnis, { code: f.code, label: f.label, qty: 1 }] });
        }
        setShowSearch(false);
    };

    const changeQty = (code, delta) => {
        const next = seg.furnis.map(x => x.code === code ? { ...x, qty: Math.max(1, x.qty + delta) } : x);
        onUpdate({ ...seg, furnis: next });
    };

    const commitLabel = () => {
        const v = labelInput.trim();
        if (v) onUpdate({ ...seg, label: v });
        setEditLabel(false);
    };

    return (
        <>
            <FurniSearchModal open={showSearch} onClose={() => setShowSearch(false)} onSelect={addFurni} />
            <div className="border border-black/20 bg-white p-2 group">
                <div className="flex items-center gap-2">
                    <div className="shrink-0 w-2 self-stretch rounded-sm" style={{ background: colorSwatch }} />
                    <div className="flex-1 min-w-0">
                        {editLabel ? (
                            <input autoFocus type="text" value={labelInput}
                                onChange={e => setLabelInput(e.target.value)}
                                onBlur={commitLabel}
                                onKeyDown={e => e.key === 'Enter' && commitLabel()}
                                className="w-full border border-black px-2 py-1 text-base font-sans bg-white focus:outline-none" />
                        ) : (
                            <button type="button" onClick={() => { setLabelInput(seg.label); setEditLabel(true); }}
                                className="font-['Jersey_10'] text-base hover:underline text-left block w-full truncate">
                                {seg.label || 'Click to set label'}
                            </button>
                        )}
                    </div>
                    <button type="button" onClick={onRemove}
                        className="shrink-0 w-7 h-7 flex items-center justify-center border border-black/20 text-black/40 hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors font-['Jersey_10'] text-lg leading-none opacity-0 group-hover:opacity-100">×</button>
                </div>
                <div className="flex flex-wrap gap-1 mt-2 pl-4">
                    {seg.furnis.map(f => (
                        <div key={f.code} className="flex items-center gap-1 border border-black/10 bg-gray-50 p-1">
                            <div className="overflow-hidden shrink-0" style={{ width: 28, height: 28 }}><FurniIcon code={f.code} size={28} /></div>
                            <span className="font-['Jersey_10'] text-base max-w-[60px] truncate">{f.label}</span>
                            <div className="flex items-center gap-0.5">
                                <button type="button" onClick={() => changeQty(f.code, -1)} className="w-6 h-6 border border-black/20 font-['Jersey_10'] text-base leading-none hover:bg-black hover:text-white flex items-center justify-center">-</button>
                                <span className="font-['Jersey_10'] text-base w-5 text-center">{f.qty}</span>
                                <button type="button" onClick={() => changeQty(f.code, 1)} className="w-6 h-6 border border-black/20 font-['Jersey_10'] text-base leading-none hover:bg-black hover:text-white flex items-center justify-center">+</button>
                            </div>
                            <button type="button" onClick={() => onUpdate({ ...seg, furnis: seg.furnis.filter(x => x.code !== f.code) })}
                                className="text-black/30 hover:text-red-500 font-['Jersey_10'] text-base leading-none ml-0.5">×</button>
                        </div>
                    ))}
                    <button type="button" onClick={() => setShowSearch(true)}
                        className="border border-dashed border-black/30 px-2 py-1 font-['Jersey_10'] text-base uppercase hover:border-black hover:bg-yellow-50 transition-colors">
                        + Add Furni
                    </button>
                </div>
            </div>
        </>
    );
}

// ─── Custom Segment Card ──────────────────────────────────────────────────────

function CustomSegmentCard({ seg, onUpdate, onRemove, colorSwatch }) {
    const [showIconPicker, setShowIconPicker] = useState(false);
    const [editText, setEditText] = useState(false);
    const [textInput, setTextInput] = useState(seg.text || '');

    const commitText = () => {
        const v = textInput.trim();
        if (v) onUpdate({ ...seg, text: v, label: v });
        setEditText(false);
    };

    return (
        <>
            <IconPickerModal open={showIconPicker} onClose={() => setShowIconPicker(false)} onSelect={n => onUpdate({ ...seg, iconNum: n })} />
            <div className="border border-black/20 bg-white flex items-center gap-2 p-2 group">
                <div className="shrink-0 w-2 self-stretch rounded-sm" style={{ background: colorSwatch }} />
                <button type="button" onClick={() => setShowIconPicker(true)} title="Click to change icon"
                    className="shrink-0 overflow-hidden flex items-center justify-center border border-black/10 hover:border-black bg-gray-50 transition-colors"
                    style={{ width: 40, height: 40 }}>
                    {seg.iconNum
                        ? <img src={`${process.env.PUBLIC_URL}/img/icons/icon_${seg.iconNum}.png`} alt="" />
                        : <span className="font-['Jersey_10'] text-base text-black/30 uppercase leading-none">Icon</span>}
                </button>
                <div className="flex-1 min-w-0">
                    {editText ? (
                        <input autoFocus type="text" value={textInput}
                            onChange={e => setTextInput(e.target.value)}
                            onBlur={commitText}
                            onKeyDown={e => e.key === 'Enter' && commitText()}
                            className="w-full border border-black px-2 py-1 text-base font-sans bg-white focus:outline-none" />
                    ) : (
                        <button type="button" onClick={() => { setTextInput(seg.text || ''); setEditText(true); }}
                            className="font-['Jersey_10'] text-base hover:underline text-left w-full truncate block">
                            {seg.text || 'Click to set text'}
                        </button>
                    )}
                </div>
                <button type="button" onClick={onRemove}
                    className="shrink-0 w-7 h-7 flex items-center justify-center border border-black/20 text-black/40 hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors font-['Jersey_10'] text-lg leading-none opacity-0 group-hover:opacity-100">×</button>
            </div>
        </>
    );
}

// ─── Section Header ───────────────────────────────────────────────────────────

function SectionHead({ children }) {
    return <h3 className="font-['Jersey_10'] uppercase text-base tracking-widest text-black/50 mb-2 mt-4 first:mt-0">{children}</h3>;
}

// ─── Overlay + JSON panels (rendered twice: desktop inside left col, mobile after settings) ──

function OverlayJsonPanels({ className, overlayUrl, copyOverlayUrl, resetWheel, downloadJson, jsonText, handleJsonTextChange, jsonError }) {
    return (
        <div className={`flex-col sm:flex-row gap-4 ${className}`}>
            <div className="flex-1 bg-coal border border-black/60 p-4" style={{ boxShadow: '3px 3px 0 rgba(0,0,0,0.4)' }}>
                <p className="font-['Jersey_10'] uppercase text-base tracking-widest text-white/60 mb-2">Stream Overlay</p>
                <p className="font-['Jersey_10'] text-base text-white/40 mb-3">
                    You can add this wheel to your streaming software as a <b>browser source</b>. Use the button below and paste the URL into a new browser source.
                </p>
                <div className="flex gap-2">
                    <input readOnly value={overlayUrl} onClick={e => e.target.select()}
                        className="flex-1 min-w-0 px-2 py-2 bg-black/30 border border-white/10 text-white/40 text-sm focus:outline-none cursor-text" />
                    <button type="button" onClick={copyOverlayUrl}
                        className="shrink-0 py-2 px-3 border-2 border-gold bg-transparent text-gold font-['Jersey_10'] text-base uppercase tracking-widest hover:bg-gold hover:text-coal transition-colors"
                        style={{ boxShadow: '2px 2px 0 rgba(0,0,0,0.5)' }}>
                        Copy URL
                    </button>
                </div>
            </div>
            <div className="flex-1 bg-coal border border-black/60 p-4" style={{ boxShadow: '3px 3px 0 rgba(0,0,0,0.4)' }}>
                <div className="flex items-center justify-between mb-2">
                    <p className="font-['Jersey_10'] uppercase text-base tracking-widest text-white/60">Wheel Data (JSON)</p>
                    <div className="flex items-center gap-2">
                        <button type="button" onClick={resetWheel}
                            className="px-2 py-1 border border-white/20 text-white/50 hover:border-red-400 hover:text-red-400 font-['Jersey_10'] text-base uppercase transition-colors">
                            Reset
                        </button>
                        <button type="button" onClick={downloadJson}
                            className="px-2 py-1 border border-white/20 text-white/50 hover:border-white hover:text-white font-['Jersey_10'] text-base uppercase transition-colors">
                            Download
                        </button>
                    </div>
                </div>
                <textarea value={jsonText} onChange={handleJsonTextChange} rows={6} spellCheck={false}
                    className="w-full bg-black/40 text-green-400 font-mono text-sm p-2 border border-white/10 focus:outline-none focus:border-white/30 resize-y habbo-scroll-v" />
                {jsonError && <p className="text-red-400 font-['Jersey_10'] text-base mt-1 uppercase">{jsonError}</p>}
            </div>
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

function loadSaved() {
    try { return JSON.parse(localStorage.getItem(LS_KEY)) || {}; } catch { return {}; }
}

export default function FranklyWheel() {
    const wheelRef = useRef(null);
    const bcRef = useRef(null);
    const bcStateTimerRef = useRef(null);
    const suppressJsonSyncRef = useRef(false);
    const spinRef = useRef(null); // always points to the latest spin fn, avoids stale closure in removeAndSpin

    const sessionKey = useMemo(() => getOrCreateSessionKey(), []);

    const saved = useMemo(loadSaved, []);

    // ── State ──
    const [mode, setMode] = useState(saved.mode || 'habbo');
    const [habboSegs, setHabboSegs] = useState(saved.habboSegs || []);
    const [furniSegs, setFurniSegs] = useState(saved.furniSegs || []);
    const [customSegs, setCustomSegs] = useState(saved.customSegs || []);
    const [colorCount, setColorCount] = useState(saved.colorCount || 2);
    const [colors, setColors] = useState(saved.colors || DEFAULT_PALETTE.slice(0, 2));
    const [fontLabel, setFontLabel] = useState(saved.fontLabel || 'Jersey 10');
    const [fontValue, setFontValue] = useState(saved.fontValue || "'Jersey 10', sans-serif");
    const [spinDuration, setSpinDuration] = useState(saved.spinDuration || 5000);
    const textSizeMul = TEXT_SIZE_MUL;
    const [textOrientation, setTextOrientation] = useState(saved.textOrientation || 'radial');
    const [winner, setWinner] = useState(null);
    const [winnerTotalCount, setWinnerTotalCount] = useState(0);
    const [isSpinning, setIsSpinning] = useState(false);
    const [habboInput, setHabboInput] = useState('');
    const [jsonText, setJsonText] = useState('');
    const [jsonError, setJsonError] = useState('');

    // ── Active segments = current mode's array ──
    const activeSegs = mode === 'habbo' ? habboSegs : mode === 'furni' ? furniSegs : customSegs;
    const setActiveSegs = mode === 'habbo' ? setHabboSegs : mode === 'furni' ? setFurniSegs : setCustomSegs;

    const wheelSegments = useMemo(() => activeSegs.filter(s => {
        if (s.type === 'habbo') return !!s.habboName;
        if (s.type === 'furni') return s.furnis?.length > 0;
        if (s.type === 'custom') return !!s.text;
        return false;
    }), [activeSegs]);

    // ── BroadcastChannel ──
    useEffect(() => {
        try { bcRef.current = new BroadcastChannel('franklywheel_spin_v1'); } catch {}
        return () => { try { bcRef.current?.close(); } catch {} };
    }, []);

    // Debounced state broadcast to overlay (BC = same browser, SSE = cross-browser/OBS)
    useEffect(() => {
        clearTimeout(bcStateTimerRef.current);
        bcStateTimerRef.current = setTimeout(() => {
            const msg = { type: 'state', segments: wheelSegments, colors, fontValue, spinDuration, textSizeMul, textOrientation };
            try { bcRef.current?.postMessage(msg); } catch {}
            postWheelMessage(sessionKey, msg);
        }, 300);
    }, [wheelSegments, colors, fontValue, spinDuration, textSizeMul, textOrientation]);

    // ── Colour count sync ──
    useEffect(() => {
        setColors(prev => {
            if (prev.length === colorCount) return prev;
            if (prev.length < colorCount) {
                const extra = Array.from({ length: colorCount - prev.length }, (_, i) => DEFAULT_PALETTE[(prev.length + i) % DEFAULT_PALETTE.length]);
                return [...prev, ...extra];
            }
            return prev.slice(0, colorCount);
        });
    }, [colorCount]);

    // ── localStorage persistence ──
    useEffect(() => {
        try {
            localStorage.setItem(LS_KEY, JSON.stringify({ mode, habboSegs, furniSegs, customSegs, colors, colorCount, fontLabel, fontValue, spinDuration, textOrientation }));
        } catch {}
    }, [mode, habboSegs, furniSegs, customSegs, colors, colorCount, fontLabel, fontValue, spinDuration, textSizeMul, textOrientation]);

    // ── JSON sync ──
    const stateToJson = useCallback(() => ({
        version: 3,
        mode,
        habboSegs,
        furniSegs,
        customSegs,
        colors,
        colorCount,
        fontLabel,
        fontValue,
        spinDuration,
        textOrientation,
    }), [mode, habboSegs, furniSegs, customSegs, colors, colorCount, fontLabel, fontValue, spinDuration, textOrientation]);

    useEffect(() => {
        if (suppressJsonSyncRef.current) { suppressJsonSyncRef.current = false; return; }
        setJsonText(JSON.stringify(stateToJson(), null, 2));
        setJsonError('');
    }, [stateToJson]);

    // ── Segment actions ──
    const addHabbo = () => {
        const name = habboInput.trim();
        if (!name) return;
        setHabboSegs(prev => [...prev, { id: uid(), type: 'habbo', habboName: name, label: name }]);
        setHabboInput('');
    };

    const addFurniSeg = () => setFurniSegs(prev => [...prev, { id: uid(), type: 'furni', label: `Prize ${prev.length + 1}`, furnis: [] }]);
    const addCustomSeg = () => setCustomSegs(prev => [...prev, { id: uid(), type: 'custom', text: '', label: '', iconNum: null }]);

    const updateSeg = useCallback((updated) => {
        const setter = updated.type === 'habbo' ? setHabboSegs : updated.type === 'furni' ? setFurniSegs : setCustomSegs;
        setter(prev => prev.map(s => s.id === updated.id ? updated : s));
    }, []);

    const removeSeg = useCallback((id, type) => {
        const setter = type === 'habbo' ? setHabboSegs : type === 'furni' ? setFurniSegs : setCustomSegs;
        setter(prev => prev.filter(s => s.id !== id));
    }, []);

    const broadcastDismiss = useCallback(() => {
        const msg = { type: 'dismiss' };
        try { bcRef.current?.postMessage(msg); } catch {}
        postWheelMessage(sessionKey, msg);
    }, [sessionKey]);

    const dismissWinner = useCallback(() => {
        setWinner(null);
        wheelRef.current?.clearWinner();
        broadcastDismiss();
    }, [broadcastDismiss]);

    const removeWinner = useCallback(() => {
        if (!winner) return;
        removeSeg(winner.id, winner.type);
        dismissWinner();
    }, [winner, removeSeg, dismissWinner]);

    const shuffle = () => {
        const arr = [...activeSegs];
        for (let i = arr.length - 1; i > 0; i--) {
            const a = new Uint32Array(1);
            crypto.getRandomValues(a);
            const j = a[0] % (i + 1);
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        setActiveSegs(arr);
        try {
            const valid = arr.filter(s => {
                if (s.type === 'habbo') return !!s.habboName;
                if (s.type === 'furni') return s.furnis?.length > 0;
                if (s.type === 'custom') return !!s.text;
                return false;
            });
            const msg = { type: 'state', segments: valid, colors, fontValue, spinDuration, textSizeMul, textOrientation };
            bcRef.current?.postMessage(msg);
            postWheelMessage(sessionKey, msg);
        } catch {}
    };

    const spin = useCallback(() => {
        if (isSpinning || wheelSegments.length < 2) return;
        const n = wheelSegments.length;
        const arr = new Uint32Array(3);
        crypto.getRandomValues(arr);
        // Scale turns with duration so all options start at a similar RPM
        const baseTurns = Math.round(spinDuration / 500); // 3s→6, 5s→10, 8s→16, 12s→24
        const extraTurnCount = baseTurns + (arr[2] % 4);
        const winnerIndex = arr[0] % n;
        const offsetFraction = arr[1] / 0x100000000;
        const startRot = wheelRef.current?.getRotation?.() ?? 0;

        // Pre-calculate totalDelta here so the overlay uses the same value regardless
        // of what segment state it currently has (fixes race condition on remove+spin).
        const arcSize = (2 * Math.PI) / n;
        const offset = arcSize * (0.1 + offsetFraction * 0.8);
        const wantedRot = -(winnerIndex * arcSize + offset);
        let diff = ((wantedRot - startRot) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
        if (diff < 0.05) diff += 2 * Math.PI;
        const totalDelta = diff + extraTurnCount * 2 * Math.PI;

        const params = { winnerIndex, offsetFraction, extraTurnCount, startRot, totalDelta };
        setIsSpinning(true);
        setWinner(null);
        setWinnerTotalCount(n);
        wheelRef.current?.spin(params);
        try { bcRef.current?.postMessage({ type: 'spin', ...params }); } catch {}
        postWheelMessage(sessionKey, { type: 'spin', ...params });

        // Broadcast 4 clock-sync checkpoints so the overlay stays frame-perfect:
        // one as the wheel enters the slow phase, then three in the final second.
        const syncAt = [
            Math.round(spinDuration * 0.55),
            spinDuration - 1000,
            spinDuration - 500,
            spinDuration - 200,
        ].filter(t => t > 0);
        syncAt.forEach(delay => {
            setTimeout(() => {
                if (!wheelRef.current?.isSpinning()) return;
                const elapsed = performance.now() - (wheelRef.current?.getSpinStartTime?.() ?? 0);
                const syncMsg = { type: 'spin_sync', elapsed };
                try { bcRef.current?.postMessage(syncMsg); } catch {}
                postWheelMessage(sessionKey, syncMsg);
            }, delay);
        });
    }, [isSpinning, wheelSegments, colors, fontValue, spinDuration, textSizeMul, textOrientation, sessionKey]); // eslint-disable-line

    // Keep spinRef always pointing at the latest spin function so removeAndSpin
    // doesn't capture a stale closure after segments change
    useEffect(() => { spinRef.current = spin; }, [spin]);

    const spinAgain = useCallback(() => {
        setWinner(null);
        setTimeout(() => spinRef.current?.(), 50);
    }, []);

    const removeAndSpin = useCallback(() => {
        if (!winner) return;
        removeSeg(winner.id, winner.type);
        setWinner(null);
        // Use spinRef so we call the spin that sees the updated (post-remove) segments
        setTimeout(() => spinRef.current?.(), 80);
    }, [winner, removeSeg]);

    const handleWinnerPicked = useCallback((idx, seg) => {
        setIsSpinning(false);
        setWinner(seg);
        // Broadcast the authoritative winner so the overlay never has to compute it locally
        const winnerMsg = { type: 'winner', seg, totalCount: wheelSegments.length };
        try { bcRef.current?.postMessage(winnerMsg); } catch {}
        postWheelMessage(sessionKey, winnerMsg);
    }, [wheelSegments.length, sessionKey]); // eslint-disable-line

    const overlayUrl = useMemo(() => {
        const encoded = encodeState({ segments: wheelSegments, colors, fontValue, spinDuration, textSizeMul, textOrientation });
        const params = new URLSearchParams({ s: encoded, key: sessionKey });
        return `${window.location.origin}/goodies/franklywheel/overlay?${params}`;
    }, [wheelSegments, colors, fontValue, spinDuration, textSizeMul, textOrientation, sessionKey]);

    const copyOverlayUrl = () => {
        navigator.clipboard.writeText(overlayUrl).then(() => toast.success('Overlay URL copied!')).catch(() => toast.error('Could not copy URL'));
    };

    const resetWheel = () => {
        if (!window.confirm('Reset the wheel to defaults? This will clear all segments and settings.')) return;
        setMode('habbo');
        setHabboSegs([]);
        setFurniSegs([]);
        setCustomSegs([]);
        setColorCount(2);
        setColors(DEFAULT_PALETTE.slice(0, 2));
        setFontLabel('Jersey 10');
        setFontValue("'Jersey 10', sans-serif");
        setSpinDuration(5000);
        setTextOrientation('radial');
    };

    const downloadJson = () => {
        const blob = new Blob([JSON.stringify(stateToJson(), null, 2)], { type: 'application/json' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'franklywheel.json';
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const handleJsonTextChange = (e) => {
        const val = e.target.value;
        suppressJsonSyncRef.current = true;
        setJsonText(val);
        try {
            applyParsedState(JSON.parse(val));
            setJsonError('');
        } catch (err) {
            setJsonError(err.message);
        }
    };

    const applyParsedState = (p) => {
        if (typeof p !== 'object' || !p) return;
        if (p.mode) setMode(p.mode);
        if (Array.isArray(p.habboSegs)) setHabboSegs(p.habboSegs.map(s => ({ ...s, id: s.id || uid() })));
        if (Array.isArray(p.furniSegs)) setFurniSegs(p.furniSegs.map(s => ({ ...s, id: s.id || uid() })));
        if (Array.isArray(p.customSegs)) setCustomSegs(p.customSegs.map(s => ({ ...s, id: s.id || uid() })));
        if (Array.isArray(p.colors) && p.colors.length) { setColors(p.colors); setColorCount(p.colors.length); }
        if (typeof p.colorCount === 'number') setColorCount(p.colorCount);
        if (p.fontValue) setFontValue(p.fontValue);
        if (p.fontLabel) setFontLabel(p.fontLabel);
        if (typeof p.spinDuration === 'number') setSpinDuration(p.spinDuration);
        if (p.textOrientation) setTextOrientation(p.textOrientation);
    };

    const segColor = (idx) => colors[idx % colors.length] || '#ccc';

    const MODE_TABS = [
        { key: 'habbo', label: 'Habbos' },
        { key: 'furni', label: 'Furnis' },
        { key: 'custom', label: 'Custom' },
    ];

    const winnerLabel = winner ? (winner.label || winner.habboName || winner.text || 'Entry') : '';
    const oddsPercent = winnerTotalCount > 0 ? (100 / winnerTotalCount).toFixed(1) : '0';

    return (
        <div className="px-3 py-4 sm:px-4 sm:py-6 max-w-[1200px] mx-auto">
            <style>{`.habbo-scroll-v::-webkit-scrollbar{width:16px}.habbo-scroll-v::-webkit-scrollbar-track{background:#cfcfcf;border:1px solid #000}.habbo-scroll-v::-webkit-scrollbar-thumb{background:#8f8f8f;border:1px solid #000}.habbo-scroll-v::-webkit-scrollbar-thumb:hover{background:#7a7a7a}`}</style>
            {/* Hidden elements to force-load custom fonts so canvas can use them */}
            <span className="font-volterBold fixed opacity-0 pointer-events-none select-none -top-full" aria-hidden="true">_</span>
            <span className="font-volter fixed opacity-0 pointer-events-none select-none -top-full" aria-hidden="true">_</span>

            <div className="mb-5">
                <h1 className="font-['Jersey_10'] uppercase text-4xl tracking-widest text-white">Frankly Wheel</h1>
            </div>

            <div className="flex flex-col lg:flex-row gap-4">

                {/* ── Left: Wheel + desktop overlay/JSON ── */}
                <div className="flex-1 min-w-0 flex flex-col gap-4">
                    <div className="relative bg-coal border border-black/60 p-4" style={{ boxShadow: '3px 3px 0 rgba(0,0,0,0.5)' }}>
                        {/* Sync overlay rotation via SSE */}
                        <button
                            onClick={() => postWheelMessage(sessionKey, { type: 'sync_idle', rotation: wheelRef.current?.getRotation() ?? 0 })}
                            title="Sync overlay wheel position"
                            className="absolute top-2 right-2 z-10 font-['Jersey_10'] text-xs uppercase tracking-wide text-white/40 hover:text-white/80 transition-colors px-2 py-1 border border-white/10 hover:border-white/30"
                        >
                            Sync
                        </button>
                        <WheelCanvas
                            ref={wheelRef}
                            segments={wheelSegments}
                            colors={colors}
                            fontValue={fontValue}
                            spinDuration={spinDuration}
                            textSizeMultiplier={textSizeMul}
                            textOrientation={textOrientation}
                            onWinnerPicked={handleWinnerPicked}
                        />

                        {/* Winner overlay on wheel */}
                        {winner && !isSpinning && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="pointer-events-auto text-center bg-offwhite"
                                     style={{
                                         border: '0.4vw solid #ffcc66',
                                         boxShadow: '0 0 2vw rgba(255,204,102,0.35), 0.5vw 0.5vw 0 rgba(0,0,0,0.55)',
                                         padding: '2vw 3vw',
                                         minWidth: '25vw',
                                         maxWidth: '82%',
                                     }}>
                                    <p style={{ fontSize: '1.2vw' }} className="font-['Jersey_10'] uppercase tracking-widest text-coal/50 mb-[0.2vw]">Winner!</p>
                                    <p style={{ fontSize: '4vw' }} className="font-['Jersey_10'] uppercase text-coal leading-tight break-words">
                                        {winnerLabel}
                                    </p>
                                    <WinnerImage seg={winner} />
                                    <p style={{ fontSize: '1.2vw' }} className="font-['Jersey_10'] text-coal/50 uppercase mt-[0.5vw]">
                                        1 in {winnerTotalCount} &nbsp;&middot;&nbsp; {oddsPercent}%
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Desktop only: overlay/JSON under wheel */}
                    <OverlayJsonPanels
                        className="hidden lg:flex"
                        overlayUrl={overlayUrl}
                        copyOverlayUrl={copyOverlayUrl}
                        resetWheel={resetWheel}
                        downloadJson={downloadJson}
                        jsonText={jsonText}
                        handleJsonTextChange={handleJsonTextChange}
                        jsonError={jsonError}
                    />
                </div>

                {/* ── Right: All controls ── */}
                <div className="lg:w-[400px] shrink-0">
                    <div className="bg-offwhite border border-black/30 p-4" style={{ boxShadow: '3px 3px 0 rgba(0,0,0,0.4)' }}>

                        {/* Spin / Shuffle */}
                        <div className="flex gap-2 mb-3">
                            <button type="button" onClick={spin} disabled={isSpinning || wheelSegments.length < 2}
                                className="flex-1 py-3 border-2 border-black bg-gold text-coal font-['Jersey_10'] text-2xl uppercase tracking-widest hover:bg-yellow-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                style={{ boxShadow: '3px 3px 0 rgba(0,0,0,0.5)' }}>
                                {isSpinning ? 'Spinning…' : wheelSegments.length < 2 ? 'Add 2+ Segments' : 'Spin!'}
                            </button>
                            <button type="button" onClick={shuffle} disabled={isSpinning || activeSegs.length < 2} title="Shuffle segment order"
                                className="px-4 py-3 border-2 border-black bg-white text-coal font-['Jersey_10'] text-base uppercase tracking-wide hover:bg-black hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                style={{ boxShadow: '3px 3px 0 rgba(0,0,0,0.5)' }}>
                                Shuffle
                            </button>
                        </div>

                        {/* Post-win actions */}
                        {winner && !isSpinning && (
                            <div className="flex flex-col gap-1.5 mb-4 p-3 border border-gold/40 bg-gold/5">
                                <p className="font-['Jersey_10'] text-base uppercase text-black/50 tracking-widest mb-1">
                                    Won: <span className="text-coal font-bold">{winnerLabel}</span>
                                </p>
                                <button type="button" onClick={spinAgain}
                                    className="w-full py-2 border-2 border-black bg-coal text-gold font-['Jersey_10'] text-lg uppercase tracking-wide hover:bg-black transition-colors"
                                    style={{ boxShadow: '2px 2px 0 rgba(0,0,0,0.5)' }}>
                                    Spin Again
                                </button>
                                <button type="button" onClick={removeAndSpin}
                                    className="w-full py-2 border-2 border-black bg-white text-coal font-['Jersey_10'] text-base uppercase tracking-wide hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors"
                                    style={{ boxShadow: '2px 2px 0 rgba(0,0,0,0.5)' }}>
                                    Remove {winnerLabel.length > 18 ? winnerLabel.slice(0, 16) + '…' : winnerLabel} &amp; Spin Again
                                </button>
                                <button type="button" onClick={removeWinner}
                                    className="w-full py-2 border border-black/30 bg-white text-coal font-['Jersey_10'] text-base uppercase tracking-wide hover:bg-black/5 transition-colors">
                                    Remove Entry
                                </button>
                                <button type="button" onClick={dismissWinner}
                                    className="w-full py-1.5 border border-black/20 bg-white text-black/40 font-['Jersey_10'] text-base uppercase tracking-wide hover:bg-black/5 transition-colors">
                                    Dismiss
                                </button>
                            </div>
                        )}

                        <hr className="border-black/15 mb-4" />

                        {/* Mode tabs */}
                        <div className="flex border-2 border-black mb-4">
                            {MODE_TABS.map(tab => (
                                <button key={tab.key} type="button" onClick={() => setMode(tab.key)}
                                    className={`flex-1 py-2 font-['Jersey_10'] uppercase text-base tracking-wide transition-colors border-r-2 border-black last:border-r-0 ${mode === tab.key ? 'bg-coal text-white' : 'bg-white text-coal hover:bg-black/5'}`}>
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* ── Habbo ── */}
                        {mode === 'habbo' && (
                            <div>
                                <div className="flex gap-2 mb-3">
                                    <input type="text" value={habboInput} onChange={e => setHabboInput(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && addHabbo()}
                                        placeholder="Habbo username…"
                                        className="flex-1 border border-black px-2 py-2 text-base font-sans bg-white focus:outline-none placeholder:text-black/30" />
                                    <button type="button" onClick={addHabbo}
                                        className="px-3 py-2 border border-black bg-coal text-white font-['Jersey_10'] text-base uppercase hover:bg-coal_dark transition-colors">
                                        Add
                                    </button>
                                </div>
                                {habboSegs.length === 0 && (
                                    <p className="text-center py-6 font-['Jersey_10'] text-base uppercase text-black/40">No Habbos added yet</p>
                                )}
                                <div className="flex flex-col gap-1.5 h-[300px] overflow-y-auto habbo-scroll-v">
                                    {habboSegs.map((seg, i) => (
                                        <HabboSegmentCard key={seg.id} seg={seg}
                                            onUpdate={updateSeg}
                                            onRemove={() => removeSeg(seg.id, 'habbo')}
                                            colorSwatch={segColor(i)} />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ── Furni ── */}
                        {mode === 'furni' && (
                            <div>
                                <button type="button" onClick={addFurniSeg}
                                    className="w-full py-2 border border-dashed border-black/40 font-['Jersey_10'] text-base uppercase hover:border-black hover:bg-black/5 transition-colors mb-3">
                                    + Add Prize Segment
                                </button>
                                {furniSegs.length === 0 && (
                                    <p className="text-center py-6 font-['Jersey_10'] text-base uppercase text-black/40">No furni segments yet</p>
                                )}
                                <div className="flex flex-col gap-1.5 h-[300px] overflow-y-auto habbo-scroll-v">
                                    {furniSegs.map((seg, i) => (
                                        <FurniSegmentCard key={seg.id} seg={seg}
                                            onUpdate={updateSeg}
                                            onRemove={() => removeSeg(seg.id, 'furni')}
                                            colorSwatch={segColor(i)} />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ── Custom ── */}
                        {mode === 'custom' && (
                            <div>
                                <button type="button" onClick={addCustomSeg}
                                    className="w-full py-2 border border-dashed border-black/40 font-['Jersey_10'] text-base uppercase hover:border-black hover:bg-black/5 transition-colors mb-3">
                                    + Add Custom Segment
                                </button>
                                {customSegs.length === 0 && (
                                    <p className="text-center py-6 font-['Jersey_10'] text-base uppercase text-black/40">No custom segments yet</p>
                                )}
                                <div className="flex flex-col gap-1.5 h-[300px] overflow-y-auto habbo-scroll-v">
                                    {customSegs.map((seg, i) => (
                                        <CustomSegmentCard key={seg.id} seg={seg}
                                            onUpdate={updateSeg}
                                            onRemove={() => removeSeg(seg.id, 'custom')}
                                            colorSwatch={segColor(i)} />
                                    ))}
                                </div>
                            </div>
                        )}

                        <hr className="border-black/15 my-4" />

                        {/* Colours */}
                        <SectionHead>Colours</SectionHead>
                        <div className="flex items-center gap-2 mb-3">
                            <span className="font-['Jersey_10'] text-base text-black/60 uppercase">Count</span>
                            <div className="flex gap-1">
                                {[2, 3, 4, 5, 6, 7, 8].map(n => (
                                    <button key={n} type="button" onClick={() => setColorCount(n)}
                                        className={`w-8 h-8 border font-['Jersey_10'] text-base transition-colors ${colorCount === n ? 'border-black bg-coal text-white' : 'border-black/30 bg-white hover:bg-black/5'}`}>
                                        {n}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {colors.map((c, i) => (
                                <div key={i} className="flex flex-col items-center gap-1">
                                    <ColorPicker value={c} onChange={e => setColors(prev => prev.map((v, j) => j === i ? e.target.value : v))}
                                        style={{ width: 32, height: 32 }} title={`Colour ${i + 1}`} />
                                    <span className="font-['Jersey_10'] text-base text-black/50">{i + 1}</span>
                                </div>
                            ))}
                        </div>

                        {/* Font */}
                        <SectionHead>Font</SectionHead>
                        <select value={fontValue} onChange={e => {
                            setFontValue(e.target.value);
                            setFontLabel(FONT_OPTIONS.find(f => f.value === e.target.value)?.label || '');
                        }} className="w-full border border-black px-2 py-2 bg-white text-base font-sans focus:outline-none cursor-pointer">
                            {FONT_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </select>

                        {/* Text Orientation */}
                        <SectionHead>Text Direction</SectionHead>
                        <div className="flex gap-1">
                            {TEXT_ORIENT_OPTIONS.map(o => (
                                <button key={o.value} type="button" onClick={() => setTextOrientation(o.value)}
                                    className={`flex-1 py-2 border font-['Jersey_10'] text-base transition-colors ${textOrientation === o.value ? 'border-black bg-coal text-white' : 'border-black/30 bg-white hover:bg-black/5'}`}>
                                    {o.label}
                                </button>
                            ))}
                        </div>

                        {/* Spin Duration */}
                        <SectionHead>Spin Duration</SectionHead>
                        <div className="flex gap-1">
                            {SPIN_DURATION_OPTIONS.map(o => (
                                <button key={o.value} type="button" onClick={() => setSpinDuration(o.value)}
                                    className={`flex-1 py-2 border font-['Jersey_10'] text-base transition-colors ${spinDuration === o.value ? 'border-black bg-coal text-white' : 'border-black/30 bg-white hover:bg-black/5'}`}>
                                    {o.label}
                                </button>
                            ))}
                        </div>

                    </div>
                </div>
            </div>

            {/* Mobile only: overlay/JSON last (after settings) */}
            <OverlayJsonPanels
                className="flex lg:hidden mt-4"
                overlayUrl={overlayUrl}
                copyOverlayUrl={copyOverlayUrl}
                downloadJson={downloadJson}
                jsonText={jsonText}
                handleJsonTextChange={handleJsonTextChange}
                jsonError={jsonError}
            />
        </div>
    );
}
